import { useState, useEffect } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ResultViewer } from "@/pages/POSRetail";
import StatisticsPanel from "@/components/StatisticsPanel";
import TabularReportsPanel from "@/components/TabularReportsPanel";
import AuditFeedPanel from "@/components/AuditFeedPanel";
import CancelledTablesPanel from "@/components/CancelledTablesPanel";
import CashDrawersPanel from "@/components/CashDrawersPanel";
import ShiftReportsPanel from "@/components/ShiftReportsPanel";
// Feb 2026 — Force Reset uses an inline numpad (unified with the
// dialog) instead of the floating Numpad component. The old import is
// no longer needed here.
import { toast } from "sonner";
import {
  FileBarChart2,
  FileText,
  Activity,
  Receipt,
  Layers,
  Printer,
  Ban,
  RotateCcw,
  BarChart3,
  ShieldAlert,
  Trash2,
  ClipboardList,
  Truck,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Wallet,
  Zap,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const ReportBtn = ({ icon: Icon, title, sub, onClick, testid, color = "blue" }) => (
  <Card
    onClick={onClick}
    data-testid={testid}
    className="cursor-pointer p-5 hover:shadow-md active:scale-[0.98] transition-all border-slate-200"
  >
    <div className="flex items-start gap-3">
      <div className={`w-12 h-12 rounded-md grid place-items-center text-white bg-${color}-600`}>
        <Icon size={22} />
      </div>
      <div>
        <div className="font-heading text-lg font-semibold">{title}</div>
        <div className="text-xs text-slate-500 mt-1">{sub}</div>
      </div>
    </div>
  </Card>
);

export default function Reports() {
  // Ιούν 2026: module «Στατιστικά» ανά κατάστημα.
  const [statsEnabled, setStatsEnabled] = useState(true);
  useEffect(() => {
    http.get("/settings")
      .then((r) => setStatsEnabled(r.data?.modules_stats !== false))
      .catch(() => {});
  }, []);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const [docCancel, setDocCancel] = useState({ open: false, day_counter: "", reason: "" });
  const [docReprint, setDocReprint] = useState({ open: false, z_report: "", day_counter: "" });
  // Snapshot of the last fiscal receipt — drives the one-tap «Επανεκτύπωση
  // Τελευταίας» card so the operator can see WHICH receipt will be
  // re-emitted (Α/Α + timestamp + amount) before tapping.
  const [lastReceipt, setLastReceipt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const r = await http.get("/reports/last-receipt-info");
        if (!cancelled) setLastReceipt(r.data);
      } catch {
        if (!cancelled) setLastReceipt(null);
      }
    };
    refresh();
    return () => {
      cancelled = true;
    };
  }, []);

  const reprintLastReceipt = async () => {
    if (!lastReceipt?.available) {
      toast.error("Δεν βρέθηκε επιτυχημένη απόδειξη");
      return;
    }
    setBusy(true);
    try {
      const r = await http.post("/reports/reprint-last-receipt");
      if (r.data.ok) {
        toast.success(`Αντίγραφο απόδειξης #${r.data.reprinted_counter} ✓`);
      } else {
        toast.error(
          `Σφάλμα: ${r.data.result?.result_description || r.data.result?.result_code || "—"}`,
        );
      }
      setResult(r.data.log);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα επανεκτύπωσης");
    } finally {
      setBusy(false);
    }
  };

  const run = async (path, body = null, method = "post") => {
    setBusy(true);
    try {
      const r = body
        ? await http[method](path, body)
        : await http[method](path);
      if (r.data.ok) {
        toast.success("Ολοκληρώθηκε");
      } else {
        toast.error(`Σφάλμα: ${r.data.result?.result_description || r.data.result?.result_code}`);
      }
      setResult(r.data.log);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="px-6 py-4 border-b border-slate-200 bg-grid">
        <h2 className="font-heading text-2xl font-bold">Αναφορές & Παραστατικά</h2>
        <div className="text-xs text-slate-500">
          Εκτέλεση εντολών αναφορών και διαχείριση παραστατικών
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scroll-thin p-6 bg-slate-50 space-y-8">
        {/* GROUP 1 - Cash register actions (was GROUP 1, now #1) */}
        <section data-testid="cash-register-section">
          <div className="flex items-center gap-2 mb-3">
            <Receipt size={18} className="text-blue-600" />
            <h3 className="font-heading text-xl font-bold">Κινήσεις Ταμειακής Μηχανής</h3>
          </div>
          <div className="text-xs text-slate-500 mb-3">
            Αναφορές και παραστατικά που εκδίδονται από την ταμειακή μηχανή (Z, X, τμήματα, ακύρωση/επανεκτύπωση).
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl">
          <ReportBtn
            icon={FileBarChart2}
            title="Αναφορά Z"
            sub="Κλείσιμο ημέρας, ZREPORT"
            onClick={() => run("/ftext/zreport")}
            testid="report-zreport"
            color="red"
          />
          <ReportBtn
            icon={FileText}
            title="Αναφορά X"
            sub="Ενημερωτική, XREPORT"
            onClick={() => run("/ftext/xreport")}
            testid="report-xreport"
            color="blue"
          />
          <ReportBtn
            icon={Layers}
            title="Τμήματα"
            sub="DREPORT"
            onClick={() => run("/ftext/dreport")}
            testid="report-dreport"
          />
          <ReportBtn
            icon={Activity}
            title="STATUS"
            sub="Κατάσταση συσκευής"
            onClick={() => run("/ftext/status")}
            testid="report-status"
          />
          <ReportBtn
            icon={Printer}
            title="Ανοιχτά Τραπέζια"
            sub="REST_PRINT_REPORT"
            onClick={() => run("/ftext/rest-print-report")}
            testid="report-rest-tables"
          />

          {/* DOC CANCEL */}
          <Dialog open={docCancel.open} onOpenChange={(o) => setDocCancel({ ...docCancel, open: o })}>
            <DialogTrigger asChild>
              <Card
                className="cursor-pointer p-5 hover:shadow-md active:scale-[0.98] transition-all border-slate-200"
                data-testid="doc-cancel-btn"
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-md grid place-items-center text-white bg-red-600">
                    <Ban size={22} />
                  </div>
                  <div>
                    <div className="font-heading text-lg font-semibold">Ακύρωση Παραστατικού</div>
                    <div className="text-xs text-slate-500 mt-1">DOC_CANCEL (εντός ημέρας)</div>
                  </div>
                </div>
              </Card>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>Ακύρωση Παραστατικού</DialogTitle>
              </DialogHeader>
              <Label>Α/Α Ημέρας</Label>
              <Input
                type="number"
                value={docCancel.day_counter}
                onChange={(e) => setDocCancel({ ...docCancel, day_counter: e.target.value })}
                data-testid="cancel-day-counter"
              />
              <Label>Αιτία</Label>
              <Input
                value={docCancel.reason}
                onChange={(e) => setDocCancel({ ...docCancel, reason: e.target.value })}
                data-testid="cancel-reason"
              />
              <DialogFooter>
                <Button
                  onClick={async () => {
                    await run("/ftext/doc-cancel", {
                      day_counter: Number(docCancel.day_counter),
                      reason: docCancel.reason,
                    });
                    setDocCancel({ open: false, day_counter: "", reason: "" });
                  }}
                  disabled={busy || !docCancel.day_counter}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="confirm-cancel-btn"
                >
                  Ακύρωση
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* REPRINT LAST RECEIPT — one-tap, no input required.
              Reads the most recent successful fiscal receipt from the
              receipt logs and fires DOC_REPRINT||<counter> at the
              register. The reprinted document is tagged «Αντίγραφο
              Φορολογικής Απόδειξης» and has no QR code (per FText
              v25.02 NOTE on DOC_REPRINT). */}
          <Card
            onClick={lastReceipt?.available ? reprintLastReceipt : undefined}
            data-testid="reprint-last-receipt-btn"
            className={`p-5 transition-all border-slate-200 ${
              lastReceipt?.available
                ? "cursor-pointer hover:shadow-md active:scale-[0.98]"
                : "opacity-50 cursor-not-allowed"
            }`}
          >
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-md grid place-items-center text-white bg-emerald-600 shrink-0">
                <Receipt size={22} />
              </div>
              <div className="min-w-0">
                <div className="font-heading text-lg font-semibold">
                  Επανεκτ. Τελευταίας
                </div>
                {lastReceipt?.available ? (
                  <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                    <div>
                      Α/Α Ημ.: <span className="font-mono font-bold">{lastReceipt.receipt_counter}</span>
                    </div>
                    {lastReceipt.device_datetime && (
                      <div className="font-mono">{lastReceipt.device_datetime}</div>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-slate-500 mt-1">
                    Καμία απόδειξη ακόμη
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* DOC REPRINT */}
          <Dialog
            open={docReprint.open}
            onOpenChange={(o) => setDocReprint({ ...docReprint, open: o })}
          >
            <DialogTrigger asChild>
              <Card
                className="cursor-pointer p-5 hover:shadow-md active:scale-[0.98] transition-all border-slate-200"
                data-testid="doc-reprint-btn"
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-md grid place-items-center text-white bg-amber-600">
                    <RotateCcw size={22} />
                  </div>
                  <div>
                    <div className="font-heading text-lg font-semibold">Επανεκτύπωση</div>
                    <div className="text-xs text-slate-500 mt-1">DOC_REPRINT</div>
                  </div>
                </div>
              </Card>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>Επανεκτύπωση Παραστατικού</DialogTitle>
              </DialogHeader>
              <Label>Α/Α Z (κενό = τρέχουσα μέρα)</Label>
              <Input
                type="number"
                value={docReprint.z_report}
                onChange={(e) => setDocReprint({ ...docReprint, z_report: e.target.value })}
                data-testid="reprint-z"
              />
              <Label>Α/Α Ημέρας</Label>
              <Input
                type="number"
                value={docReprint.day_counter}
                onChange={(e) => setDocReprint({ ...docReprint, day_counter: e.target.value })}
                data-testid="reprint-day-counter"
              />
              <DialogFooter>
                <Button
                  onClick={async () => {
                    await run("/ftext/doc-reprint", {
                      z_report: docReprint.z_report ? Number(docReprint.z_report) : null,
                      day_counter: Number(docReprint.day_counter),
                    });
                    setDocReprint({ open: false, z_report: "", day_counter: "" });
                  }}
                  disabled={busy || !docReprint.day_counter}
                  data-testid="confirm-reprint-btn"
                >
                  Επανεκτύπωση
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {result && (
          <div className="mt-6 max-w-3xl">
            <Card className="p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">
                Τελευταίο Αποτέλεσμα
              </div>
              <ResultViewer log={result} />
            </Card>
          </div>
        )}
        </section>

        {/* GROUP 2 - Shift management (per-operator daily stats,
            close-shift, X/Z reports, order-counter reset). */}
        <section data-testid="shift-reports-section">
          <div className="flex items-center gap-2 mb-3">
            <ClipboardList size={18} className="text-indigo-600" />
            <h3 className="font-heading text-xl font-bold">Βάρδιες & Κλείσιμο Ημέρας</h3>
          </div>
          <div className="text-xs text-slate-500 mb-3">
            Στατιστικά ανά χειριστή / ημέρα, drill-down στις παραγγελίες,
            διορθώσεις & εκπτώσεις, εκτύπωση «X» στιγμιοτύπου ή «Z»
            (κλείσιμο βάρδιας με μηδενισμό). <b>Δεν επικοινωνεί με την
            ταμειακή</b> — τα κουμπιά X/Z της ταμειακής βρίσκονται στην
            ενότητα «Κινήσεις Ταμειακής Μηχανής».
          </div>
          <ShiftReportsPanel />
        </section>

        {/* GROUP 3 - Delivery code counter management */}
        <section data-testid="delivery-code-section">
          <div className="flex items-center gap-2 mb-3">
            <Truck size={18} className="text-blue-600" />
            <h3 className="font-heading text-xl font-bold">Κωδικός Παραγγελιών Διανομής</h3>
          </div>
          <div className="text-xs text-slate-500 mb-3">
            Ο 4-ψήφιος κωδικός που εκτυπώνεται στο barcode κάθε προφόρμας
            διανομής (μορφή <code className="px-1 bg-slate-100 rounded">7777ΧΧΧΧ</code>).
            Μηδενίζεται μόνο χειροκίνητα — η επόμενη παραγγελία θα πάρει κωδικό
            «0001».
          </div>
          <DeliveryCodeCounter />
        </section>

        {/* GROUP 4 - Ταμείο — Σήμερα (pulled out of Statistics so it stays
            visible without expanding the collapsibles below). */}
        <section data-testid="cash-drawers-section">
          <div className="flex items-center gap-2 mb-3">
            <Wallet size={18} className="text-indigo-600" />
            <h3 className="font-heading text-xl font-bold">Ταμείο — Σήμερα</h3>
          </div>
          <div className="text-xs text-slate-500 mb-3">
            Ταμείο ημέρας (φορολογικές εισπράξεις vs απλές παραγγελίες), pie chart, breakdown ανά τρόπο πληρωμής.
          </div>
          <CashDrawersPanel />
        </section>

        {/* GROUP 5 - Statistics (collapsed by default, hidden όταν το
            module «Στατιστικά» είναι ανενεργό για το κατάστημα) */}
        {statsEnabled && (
        <CollapsibleSection
          testid="statistics-section"
          icon={BarChart3}
          iconColor="text-emerald-600"
          title="Στατιστικά Προγράμματος"
          subtitle="Αναλυτικά στατιστικά κίνησης ειδών, κατηγοριών και αποδείξεων."
        >
          <div className="space-y-6">
            <TabularReportsPanel />
            <StatisticsPanel />
          </div>
        </CollapsibleSection>
        )}

        {/* GROUP 6 - Audit feed (collapsed by default) */}
        <CollapsibleSection
          testid="audit-feed-section"
          icon={ShieldAlert}
          iconColor="text-red-600"
          title="Αναφορά Ελέγχου"
          subtitle="Audit trail κρίσιμων ενεργειών — διαγραφές τραπεζιών, ακυρώσεις γραμμών, μεταφορές, μερικές πληρωμές, Z/X αναφορές, login/βάρδιες."
        >
          <AuditFeedPanel />
        </CollapsibleSection>

        {/* GROUP 7 - Cancelled Tables (collapsed by default) */}
        <CollapsibleSection
          testid="cancelled-tables-section"
          icon={Trash2}
          iconColor="text-red-600"
          title="Ακυρωμένα Τραπέζια"
          subtitle="Όλες οι διαγραφές τραπεζιών — ποιος, πότε, ποιο τραπέζι, πόσα €. Κλικ στο βελάκι για να δεις τα είδη που ακυρώθηκαν."
        >
          <CancelledTablesPanel />
        </CollapsibleSection>

        {/* GROUP 8 - Force Reset (destructive, panic-button) */}
        <ForceResetPanel />
      </div>
    </div>
  );
}


/**
 * Collapsible section wrapper — mirrors the visual language of the
 * non-collapsible groups but with a chevron-toggled header.
 */
function CollapsibleSection({ testid, icon: Icon, iconColor, title, subtitle, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section data-testid={testid}>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center gap-2 mb-3 text-left hover:bg-slate-100 rounded-md px-2 py-1.5 -mx-2 transition-colors"
            data-testid={`${testid}-toggle`}
          >
            {open ? (
              <ChevronDown size={18} className="text-slate-500 shrink-0" />
            ) : (
              <ChevronRight size={18} className="text-slate-500 shrink-0" />
            )}
            <Icon size={18} className={`${iconColor} shrink-0`} />
            <h3 className="font-heading text-xl font-bold">{title}</h3>
          </button>
        </CollapsibleTrigger>
        {subtitle && !open && (
          <div className="text-xs text-slate-500 mb-3 pl-9">{subtitle}</div>
        )}
        <CollapsibleContent>
          {subtitle && (
            <div className="text-xs text-slate-500 mb-3 pl-9">{subtitle}</div>
          )}
          {children}
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}


function DeliveryCodeCounter() {
  const [counter, setCounter] = useState({ current: 0, next: 1 });
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await http.get("/delivery/code-counter");
      setCounter(r.data);
    } catch (e) {
      toast.error("Αποτυχία φόρτωσης κωδικού");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const reset = async () => {
    setBusy(true);
    try {
      await http.post("/delivery/reset-code-counter");
      toast.success("Ο κωδικός διανομής μηδενίστηκε. Επόμενη παραγγελία: 0001");
      setConfirmOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία μηδενισμού");
    } finally {
      setBusy(false);
    }
  };

  const fmt = (n) => String(n).padStart(4, "0");

  return (
    <Card className="p-5 max-w-2xl">
      {counter.warning_level === "critical" && (
        <div className="mb-4 px-3 py-2 rounded bg-red-50 border-2 border-red-300 text-red-800 text-sm font-bold flex items-center gap-2" data-testid="delivery-code-banner-critical">
          ⛔ Ο κωδικός έφτασε στο όριο των 4 ψηφίων. Μηδενίστε άμεσα.
        </div>
      )}
      {counter.warning_level === "warning" && (
        <div className="mb-4 px-3 py-2 rounded bg-amber-50 border-2 border-amber-300 text-amber-900 text-sm font-bold flex items-center gap-2" data-testid="delivery-code-banner-warning">
          ⚠️ Ο κωδικός πλησιάζει στο όριο 9999. Σκεφτείτε μηδενισμό.
        </div>
      )}
      <div className="grid grid-cols-2 gap-6 mb-5">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1">
            Τελευταίος Κωδικός
          </div>
          <div
            className="font-mono font-black text-5xl text-slate-800 tabular-nums"
            data-testid="delivery-code-current"
          >
            {loading ? "—" : fmt(counter.current)}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1">
            Επόμενος Κωδικός
          </div>
          <div
            className="font-mono font-black text-5xl text-emerald-700 tabular-nums"
            data-testid="delivery-code-next"
          >
            {loading ? "—" : fmt(counter.next)}
          </div>
          <div className="text-[10px] text-slate-400 font-mono mt-1">
            Barcode: 7777{loading ? "----" : fmt(counter.next)}
          </div>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            className="h-11 border-2 border-red-300 text-red-700 hover:bg-red-50 font-bold"
            data-testid="delivery-code-reset-btn"
            disabled={busy}
          >
            <RotateCcw size={16} className="mr-2" />
            Μηδενισμός Κωδικού Παραγγελιών
          </Button>
        </DialogTrigger>
        <DialogContent data-testid="delivery-code-reset-dialog">
          <DialogHeader>
            <DialogTitle>Μηδενισμός Κωδικού Διανομής</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 py-2">
            Ο κωδικός θα επιστρέψει στο <span className="font-mono font-bold">0000</span>{" "}
            και η <em>επόμενη</em> παραγγελία θα πάρει κωδικό{" "}
            <span className="font-mono font-bold text-emerald-700">0001</span>.
            <br />
            <br />
            Παλιές παραγγελίες διατηρούν τον κωδικό τους — η ενέργεια αφορά μόνο
            μελλοντικές καταχωρήσεις.
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              data-testid="delivery-code-reset-cancel"
              disabled={busy}
            >
              Άκυρο
            </Button>
            <Button
              onClick={reset}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delivery-code-reset-confirm"
              disabled={busy}
            >
              {busy ? "Μηδενισμός…" : "Ναι, μηδενισμός"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// ────────────────────────────────────────────────────────────────
// FORCE RESET PANEL — irreversible dataset wipes, guarded by admin_code
// ────────────────────────────────────────────────────────────────
const FORCE_RESET_SCOPES = [
  {
    key: "open_tables",
    label: "Ανοιχτά Τραπέζια",
    desc: "Σβήνει όλα τα ανοιχτά τραπέζια χωρίς να ασχολείται με την ταμειακή μηχανή.",
  },
  {
    key: "sales",
    label: "Πωλήσεις",
    desc: "Σβήνει όλες τις πωλήσεις (αποδείξεις).",
  },
  {
    key: "history",
    label: "Ιστορικό & Logs",
    desc: "Σβήνει όλο το ιστορικό (audit events + app logs).",
  },
  {
    key: "customer_activity",
    label: "Κινήσεις Πελατών",
    desc: "Σβήνει όλες τις κινήσεις των πελατών (το πελατολόγιο παραμένει).",
  },
  {
    key: "delivery_orders",
    label: "Παραγγελίες Delivery",
    desc: "Σβήνει όλες τις παραγγελίες Delivery, ανοιχτές και κλειστές.",
  },
  {
    key: "shifts",
    label: "Βάρδιες & Ταμεία",
    desc: "Σβήνει όλες τις βάρδιες, τα κλεισίματα καταστήματος και τα ταμεία οδηγών.",
  },
  {
    key: "print_queue",
    label: "Ουρά & Ιστορικό Εκτυπώσεων",
    desc: "Σβήνει την ουρά εκτυπώσεων, τα fiscal jobs και τα αρχεία FTEXT outbox.",
  },
  {
    key: "messages",
    label: "Μηνύματα Χειριστών",
    desc: "Σβήνει όλα τα μηνύματα μεταξύ χειριστών.",
  },
  {
    key: "security_log",
    label: "Αρχείο Ασφαλείας",
    desc: "Σβήνει το ιστορικό προσπαθειών σύνδεσης και τις μαθημένες προτάσεις.",
  },
  {
    key: "counters",
    label: "Αρίθμηση & Μετρητές",
    desc: "Μηδενίζει τον Αρ. Παραγγελίας και όλους τους μετρητές (delivery/driver codes).",
  },
];

function ForceResetPanel() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("code"); // "code" | "pick" | "confirm"
  const [adminCode, setAdminCode] = useState("");
  const [selected, setSelected] = useState({}); // key -> bool
  const [factory, setFactory] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep("code");
    setAdminCode("");
    setSelected({});
    setFactory(false);
    setBusy(false);
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) reset();
  };

  const submitCode = () => {
    if (!adminCode.trim()) {
      toast.error("Συμπλήρωσε τον κωδικό διαχειριστή");
      return;
    }
    setStep("pick");
  };

  const toggleScope = (key) =>
    setSelected((s) => ({ ...s, [key]: !s[key] }));

  const selectedKeys = FORCE_RESET_SCOPES.filter((s) => selected[s.key]).map((s) => s.key);
  const allChecked = selectedKeys.length === FORCE_RESET_SCOPES.length;

  const toggleAll = () => {
    if (allChecked) {
      setSelected({});
    } else {
      const all = {};
      FORCE_RESET_SCOPES.forEach((s) => { all[s.key] = true; });
      setSelected(all);
    }
  };

  const goConfirm = (isFactory) => {
    if (isFactory) {
      setFactory(true);
      setStep("confirm");
      return;
    }
    if (selectedKeys.length === 0) {
      toast.error("Επίλεξε τουλάχιστον ένα πεδίο");
      return;
    }
    setFactory(false);
    setStep("confirm");
  };

  const confirmWipe = async () => {
    setBusy(true);
    try {
      const payload = factory
        ? { admin_code: adminCode, scopes: ["factory"] }
        : { admin_code: adminCode, scopes: selectedKeys };
      const r = await http.post("/admin/force-reset-batch", payload);
      if (r.data?.ok) {
        toast.success(
          factory
            ? `Πλήρης επαναφορά ολοκληρώθηκε — μηδενίστηκαν ${r.data.total_deleted} εγγραφές`
            : `Ολοκληρώθηκε — μηδενίστηκαν ${r.data.total_deleted} εγγραφές`,
        );
        handleOpenChange(false);
      } else {
        toast.error("Αποτυχία μηδενισμού");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Αποτυχία μηδενισμού";
      const isPreflight = e?.response?.status === 409;
      toast.error(detail, isPreflight ? { duration: 8000 } : undefined);
      if (isPreflight) {
        handleOpenChange(false);
      } else if (detail.toLowerCase().includes("λάθος") || detail.toLowerCase().includes("κωδικός")) {
        setStep("code");
        setAdminCode("");
      }
    } finally {
      setBusy(false);
    }
  };

  const confirmLabels = factory
    ? ["Όλα τα δεδομένα χρήσης (σαν καινούριο)"]
    : FORCE_RESET_SCOPES.filter((s) => selected[s.key]).map((s) => s.label);

  return (
    <section data-testid="force-reset-section" className="pt-4 border-t-2 border-red-100">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle size={18} className="text-red-600" />
        <h3 className="font-heading text-xl font-bold text-red-700">Force Reset</h3>
      </div>
      <div className="text-xs text-slate-500 mb-3">
        Μαζικός μηδενισμός δεδομένων. Επίλεξε με check ποια θέλεις (ή όλα) και βάλε τον
        κωδικό <b>μία φορά</b>. Απαιτεί τον κωδικό διαχειριστή.{" "}
        <b className="text-red-700">Μη επιστρέψιμη ενέργεια.</b>
      </div>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            className="h-11 border-2 border-red-400 text-red-700 hover:bg-red-50 font-bold"
            data-testid="force-reset-open-btn"
          >
            <AlertTriangle size={16} className="mr-2" />
            Force Reset
          </Button>
        </DialogTrigger>
        <DialogContent
          className="max-w-md"
          data-testid="force-reset-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle size={18} /> Force Reset
            </DialogTitle>
          </DialogHeader>

          {step === "code" && (
            <div className="space-y-3 py-2" data-testid="force-reset-step-code">
              <Label>Κωδικός Διαχειριστή</Label>
              <div
                className="rounded-lg border-2 border-slate-300 bg-slate-50 p-3 text-center font-mono text-3xl tracking-[0.4em] text-slate-800 select-none min-h-[52px]"
                data-testid="force-reset-code-display"
              >
                {adminCode ? "•".repeat(adminCode.length) : (
                  <span className="text-slate-400 text-lg tracking-normal">
                    Πληκτρολογήστε κωδικό
                  </span>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2" data-testid="force-reset-numpad">
                {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((k) => (
                  <button
                    type="button"
                    key={k}
                    onClick={() => setAdminCode((v) => (v + k).slice(0, 20))}
                    data-testid={`force-reset-key-${k}`}
                    className="h-14 rounded-lg border-2 border-slate-300 bg-white hover:bg-slate-100 active:scale-95 active:bg-slate-200 text-2xl font-bold text-slate-800 transition-all shadow-sm"
                  >
                    {k}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setAdminCode("")}
                  data-testid="force-reset-key-clear"
                  className="h-14 rounded-lg border-2 border-amber-300 bg-amber-50 hover:bg-amber-100 active:scale-95 text-sm font-bold text-amber-800 transition-all shadow-sm"
                >
                  ΚΑΘΑΡ.
                </button>
                <button
                  type="button"
                  onClick={() => setAdminCode((v) => (v + "0").slice(0, 20))}
                  data-testid="force-reset-key-0"
                  className="h-14 rounded-lg border-2 border-slate-300 bg-white hover:bg-slate-100 active:scale-95 active:bg-slate-200 text-2xl font-bold text-slate-800 transition-all shadow-sm"
                >
                  0
                </button>
                <button
                  type="button"
                  onClick={() => setAdminCode((v) => v.slice(0, -1))}
                  data-testid="force-reset-key-backspace"
                  className="h-14 rounded-lg border-2 border-slate-300 bg-slate-100 hover:bg-slate-200 active:scale-95 text-lg font-bold text-slate-700 transition-all shadow-sm"
                >
                  ⌫
                </button>
              </div>

              <div className="text-[11px] text-slate-500 pt-1">
                Ο κωδικός ορίζεται από τον Supervisor. Αν δεν υπάρχει, ρώτα τον διαχειριστή.
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  data-testid="force-reset-cancel"
                >
                  Άκυρο
                </Button>
                <Button
                  onClick={submitCode}
                  className="bg-red-600 hover:bg-red-700"
                  disabled={!adminCode.trim()}
                  data-testid="force-reset-continue"
                >
                  Συνέχεια
                </Button>
              </DialogFooter>
            </div>
          )}

          {step === "pick" && (
            <div className="space-y-2 py-2" data-testid="force-reset-step-pick">
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm text-slate-600">
                  Επίλεξε τι θέλεις να μηδενίσεις:
                </div>
                <button
                  type="button"
                  onClick={toggleAll}
                  data-testid="force-reset-select-all"
                  className="text-xs font-bold text-blue-700 hover:underline"
                >
                  {allChecked ? "Καθαρισμός επιλογής" : "Επιλογή όλων"}
                </button>
              </div>

              <div className="max-h-[320px] overflow-y-auto scroll-thin space-y-1.5 pr-1">
                {FORCE_RESET_SCOPES.map((s) => (
                  <label
                    key={s.key}
                    className="flex items-start gap-3 p-2.5 rounded-lg border-2 border-slate-200 hover:border-red-300 cursor-pointer transition-all"
                    data-testid={`force-reset-scope-${s.key}`}
                  >
                    <Checkbox
                      checked={!!selected[s.key]}
                      onCheckedChange={() => toggleScope(s.key)}
                      className="mt-0.5"
                      data-testid={`force-reset-check-${s.key}`}
                    />
                    <div>
                      <div className="font-bold text-slate-800 text-sm">{s.label}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{s.desc}</div>
                    </div>
                  </label>
                ))}
              </div>

              {/* Factory reset — «σαν καινούριο» */}
              <div className="mt-3 rounded-lg border-2 border-red-300 bg-red-50 p-3">
                <div className="flex items-center gap-2 text-red-800 font-bold text-sm mb-1">
                  <Zap size={16} /> Πλήρης Επαναφορά «σαν καινούριο»
                </div>
                <div className="text-xs text-red-700 mb-2">
                  Σβήνει ΟΛΑ τα δεδομένα χρήσης (πωλήσεις, ιστορικό, βάρδιες, εκτυπώσεις,
                  μηνύματα, μετρητές…) και αφήνει το πρόγραμμα σαν να μη δούλεψε ποτέ.
                  Διατηρεί: είδη/τιμές, <b>απόθεμα ειδών</b>, τραπέζια, χειριστές, εκτυπωτές, ρυθμίσεις, πελατολόγιο.
                </div>
                <Button
                  onClick={() => goConfirm(true)}
                  className="w-full bg-red-700 hover:bg-red-800 text-white font-bold"
                  data-testid="force-reset-factory-btn"
                >
                  <Zap size={15} className="mr-1.5" /> Πλήρης Επαναφορά
                </Button>
              </div>

              <DialogFooter className="mt-2">
                <Button
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  data-testid="force-reset-cancel-pick"
                >
                  Άκυρο
                </Button>
                <Button
                  onClick={() => goConfirm(false)}
                  className="bg-red-600 hover:bg-red-700"
                  disabled={selectedKeys.length === 0}
                  data-testid="force-reset-continue-pick"
                >
                  Συνέχεια ({selectedKeys.length})
                </Button>
              </DialogFooter>
            </div>
          )}

          {step === "confirm" && (
            <div className="space-y-3 py-2" data-testid="force-reset-step-confirm">
              <div className="rounded-md border-2 border-red-300 bg-red-50 p-4">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle size={20} className="text-red-600 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-bold text-red-800">
                      {factory ? "Πλήρης επαναφορά — μη επιστρέψιμη!" : "Μη επιστρέψιμη κίνηση"}
                    </div>
                    <div className="text-sm text-red-700 mt-1">
                      Θα διαγραφούν οριστικά:
                    </div>
                    <ul className="text-sm text-red-700 mt-1 list-disc list-inside space-y-0.5">
                      {confirmLabels.map((l) => (
                        <li key={l}>{l}</li>
                      ))}
                    </ul>
                    <div className="text-xs text-red-600 mt-2">
                      Δεν υπάρχει δυνατότητα ανάκτησης.
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setStep("pick")}
                  disabled={busy}
                  data-testid="force-reset-back"
                >
                  Πίσω
                </Button>
                <Button
                  onClick={confirmWipe}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={busy}
                  data-testid="force-reset-confirm"
                >
                  {busy ? "Μηδενισμός…" : (factory ? "Ναι, πλήρης επαναφορά" : "Ναι, μηδένισε")}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
